import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { eventAPI } from '../services/eventApi';
import { AppEventDetail } from '../types/event';
import EventHeader from '../components/events/EventHeader';
import EventDetails from '../components/events/EventDetails';
import EventActions from '../components/events/EventActions';
import EventRecording from '../components/events/EventRecording';
import ResourceContent from '../components/resources/ResourceContent';
import Breadcrumb from '../components/layout/Breadcrumb';
import { ON_THIS_PAGE_LAYOUT } from '../components/layout/OnThisPage';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';

const PAGE_SHELL = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8';

export default function EventDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const playRecording = searchParams.get('play') === '1';
  const [event, setEvent] = useState<AppEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageTitle(event?.title);

  useEffect(() => {
    if (!slug) {
      setError(new Error('Invalid event slug'));
      setLoading(false);
      return;
    }

    let cancelled = false;
    eventAPI
      .fetchEventBySlug(slug)
      .then((loaded) => {
        if (!cancelled) setEvent(loaded);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to load event'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className={PAGE_SHELL}>
        <LoadingSkeleton lines={10} className="mb-4" />
        <LoadingSkeleton lines={5} />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={PAGE_SHELL}>
        <ErrorState
          error={error || new Error('Event not found')}
          title="Event Not Found"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className={PAGE_SHELL}>
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Events', to: '/events' },
          { label: event.title },
        ]}
      />

      <Link
        to="/events"
        className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to Events</span>
      </Link>

      <article>
        <EventHeader event={event} />
        <div className={ON_THIS_PAGE_LAYOUT}>
          <EventActions event={event} />
          <div className="min-w-0">
            <EventDetails event={event} />
            {event.recordingUrl && (
              <EventRecording url={event.recordingUrl} title={event.title} autoplay={playRecording} />
            )}
            <ResourceContent content={event.description} />
          </div>
        </div>
      </article>
    </div>
  );
}
